/**
 * Catalogue of database engines / connectivity targets shown in the "Add data
 * source → Database" Engine dropdown.
 *
 * NOTE: Only MySQL and PostgreSQL are wired end-to-end today. The rest are
 * listed so the product surface reflects the intended coverage; selecting one
 * sets a sensible default port but the actual ingestion drivers are TODO.
 *
 * `port` is the conventional default port (0 = not port-based, e.g. file or
 * cloud HTTP endpoints).
 */
export const DB_ENGINES = [
  // Relational — widely used
  { label: 'MySQL', value: 'mysql', port: 3306 },
  { label: 'PostgreSQL', value: 'pgsql', port: 5432 },
  { label: 'Microsoft SQL Server (MSSQL)', value: 'mssql', port: 1433 },
  { label: 'Oracle Database', value: 'oracle', port: 1521 },
  { label: 'MariaDB', value: 'mariadb', port: 3306 },
  { label: 'IBM Db2', value: 'db2', port: 50000 },
  { label: 'SAP Sybase ASE', value: 'sybase', port: 5000 },
  { label: 'Firebird', value: 'firebird', port: 3050 },
  { label: 'CockroachDB', value: 'cockroachdb', port: 26257 },
  { label: 'SQLite', value: 'sqlite', port: 0 },
  { label: 'DuckDB', value: 'duckdb', port: 0 },

  // SAP
  { label: 'SAP HANA', value: 'sap_hana', port: 30015 },
  { label: 'SAP (S/4HANA Connectivity)', value: 'sap', port: 3300 },
  { label: 'SAP BW', value: 'sap_bw', port: 3300 },

  // Cloud data warehouses
  { label: 'Snowflake', value: 'snowflake', port: 443 },
  { label: 'Google BigQuery', value: 'bigquery', port: 443 },
  { label: 'Amazon Redshift', value: 'redshift', port: 5439 },
  { label: 'Amazon Aurora', value: 'aurora', port: 3306 },
  { label: 'Azure Synapse Analytics', value: 'synapse', port: 1433 },
  { label: 'Azure SQL Database', value: 'azure_sql', port: 1433 },
  { label: 'Databricks', value: 'databricks', port: 443 },
  { label: 'Google Cloud Spanner', value: 'spanner', port: 443 },
  { label: 'Teradata', value: 'teradata', port: 1025 },
  { label: 'Vertica', value: 'vertica', port: 5433 },
  { label: 'Greenplum', value: 'greenplum', port: 5432 },
  { label: 'IBM Netezza', value: 'netezza', port: 5480 },

  // Analytical / big data engines
  { label: 'ClickHouse', value: 'clickhouse', port: 8123 },
  { label: 'Trino / Presto', value: 'trino', port: 8080 },
  { label: 'Apache Hive', value: 'hive', port: 10000 },
  { label: 'Apache Impala', value: 'impala', port: 21050 },
  { label: 'Apache Druid', value: 'druid', port: 8082 },
  { label: 'Apache Spark SQL', value: 'spark_sql', port: 10000 },

  // NoSQL / search
  { label: 'MongoDB', value: 'mongodb', port: 27017 },
  { label: 'Apache Cassandra', value: 'cassandra', port: 9042 },
  { label: 'Elasticsearch', value: 'elasticsearch', port: 9200 },
  { label: 'Couchbase', value: 'couchbase', port: 8091 },
  { label: 'Redis', value: 'redis', port: 6379 },
  { label: 'Neo4j', value: 'neo4j', port: 7687 },

  // SaaS / application connectivity
  { label: 'Salesforce', value: 'salesforce', port: 443 },
  { label: 'ServiceNow', value: 'servicenow', port: 443 },
  { label: 'Workday', value: 'workday', port: 443 },
  { label: 'NetSuite', value: 'netsuite', port: 443 },
  { label: 'Microsoft Dynamics 365', value: 'dynamics365', port: 443 },
  { label: 'REST API', value: 'rest', port: 443 },
  { label: 'OData', value: 'odata', port: 443 },
  { label: 'GraphQL', value: 'graphql', port: 443 },
] as const;

export type DbEngine = (typeof DB_ENGINES)[number]['value'];

/** Conventional default port for an engine (falls back to 5432). */
export function defaultPort(engine: string): number {
  return DB_ENGINES.find((e) => e.value === engine)?.port ?? 5432;
}
